/**
 * =============================================================================
 *
 * @format
 */

/*===============================  IMPORTS  ===================================*/
/*=============================================================================*/
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Customize_Reworked from "./index";
import LoadingOverlay from "react-loading-overlay";
import { customizeProduct, getBottomBars } from "../../actions/blindActions";
import { description, title } from "../../constants/tooltipConstants";
import { Info, Ruler } from "lucide-react";
import { motion } from "framer-motion";
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";

/*=============================================================================*/
/*===============================  MAP COLORS =================================*/
/*=============================================================================*/

const Customize_BottomBar = () => {
	/*=============================================================================*/
	/*===============================  VARIABLES  =================================*/
	/*=============================================================================*/
	const dispatch = useDispatch();
	const { productInfo, optionInfo } = useSelector(
		(state) => state.customize.customizeProduct
	);
	const [bottomBar, setBottomBar] = useState(productInfo.Bottom_Bar);
	const [bottomBarColor, setBottomBarColor] = useState(
		productInfo.Bottom_Bar_Color
	);
	const [selectedWrapType, setSelectedWrapType] = useState(
		productInfo.Hem_Wrap_Type
	);
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([]);
	const shadeTitle = productInfo.Type.toLowerCase() + " shade";

	/*=============================================================================*/
	/*===============================  USE EFFECT  ================================*/
	/*=============================================================================*/
	useEffect(() => {
		loadBottomBars();
	}, []);

	useEffect(() => {
		dispatchDetails();
	}, [bottomBar, bottomBarColor]);

	/*=============================================================================*/
	/*===============================  FUNCTIONS  =================================*/
	/*=============================================================================*/
	const isSelected = (product) => {
		return product === bottomBar.toLowerCase().replace("front wrapped ", "");
	};

	const dispatchDetails = () => {
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					Bottom_Bar: bottomBar,
					Bottom_Bar_Color: bottomBarColor,
					Hem_Wrap_Type: selectedWrapType,
				},
				optionInfo: {
					...optionInfo,
				},
			})
		);
	};

	const loadBottomBars = async () => {
		try {
			let res = await getBottomBars(
				shadeTitle,
				bottomBar,
				productInfo.defaultBottomBar
			);

			console.log(res);

			setBottomBarColor(bottomBarColor);

			res = res.filter((index) => {
				return !index.hem_type.includes("Wrapped");
			});

			setData(res);
			setLoading(false);
		} catch (error) {
			console.log(error);
			// TODO - Error handling
			alert("Error loading bottom bars");
		}
	};

	const handleBottomBar = (id, e) => {
		if (
			e.target.parentNode == document.getElementById("bcolours") ||
			e.target.parentNode == document.getElementById("bwrap")
		)
			return;
		setBottomBar(id);
		if (id === "Plain Hem") {
			setBottomBarColor("Standard");
		} else {
			// Set it to first color if bottomBarColor is not in the list
			const colours = data.find((item) => item.hem_type === id).colour;

			if (!colours.includes(bottomBarColor)) {
				setBottomBarColor(colours[0]);
			}
		}
	};

	const handleBottomBarColor = (e) => {
		setBottomBarColor(e.target.value);
	};

	const handleWrapType = (e) => {
		setSelectedWrapType(e.target.value);
		e.target.value === "No"
			? setBottomBar(bottomBar.replace("Front Wrapped Accubar", "Accubar"))
			: setBottomBar(bottomBar.replace("Accubar", "Front Wrapped Accubar"));
	};

	function getHemTypeDisplayName(hemType) {
		if (hemType === "Interlude") {
			return "Banded Bar";
		} else if (hemType === "Interlude 2.0") {
			return "Aluminum Bar";
		} else if (hemType === "Accubar 2.1") return "Aluminum Hem";
		else if (hemType === "Illusion") return "Aluminum Bar";
		return hemType;
	}

	/*=============================================================================*/
	/*===============================  RENDER  ====================================*/
	/*=============================================================================*/
	return (
		<Customize_Reworked>
			<LoadingOverlay
				fadeSpeed={200}
				active={loading}
				spinner
				text="Loading..."
				styles={{
					overlay: (base) => ({
						...base,
						background: "white",
					}),
					spinner: (base) => ({
						...base,
						width: "150px",
						height: "150px",
						"& svg circle": {
							stroke: "transparent",
						},
						backgroundImage: "url(/images/logoDark.png)",
						backgroundRepeat: "no-repeat",
						backgroundPosition: "center",
						backgroundSize: "contain",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						margin: "auto",
					}),
					content: (base) => ({
						...base,
						color: "black",
						fontSize: "18px",
						fontWeight: "500",
					}),
				}}
			>
				<div className="space-y-8">
					{/* Header Section */}
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
							<Ruler className="w-6 h-6 text-primary" />
						</div>
						<h3 className="text-lg font-medium">Select Bottom Bar</h3>
					</div>

					{/* Options Grid */}
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{data.map((item) => (
							<motion.div
								key={item.hem_type}
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className={`
					relative p-4 border-2 rounded-2xl cursor-pointer transition-all
					${
						isSelected(item.hem_type.toLowerCase())
							? "border-primary bg-primary/5 shadow-lg shadow-primary/10"
							: "border-gray-200 hover:border-primary/30"
					}
				  `}
							>
								{/* Image Section */}
								<div className="rounded-lg overflow-hidden mb-4 relative">
									<ImageZoomWithButton
										item={{
											image: item.hem_image,
											colour: item.colour,
										}}
										isFabricSelected={false}
										customize={true}
									/>
								</div>

								{/* Content Section */}
								<div className="space-y-4">
									<div className="flex items-center gap-2">
										<input
											aria-label={item.hem_type}
											type="radio"
											name="bottomBar"
											id={item.hem_type}
											checked={isSelected(item.hem_type.toLowerCase())}
											onChange={(e) => handleBottomBar(item.hem_type, e)}
											className="w-4 h-4 text-primary"
										/>
										<label
											htmlFor={item.hem_type}
											className="font-medium flex items-center gap-2"
										>
											{getHemTypeDisplayName(item.hem_type)}
											<button
												className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100"
												title={`${title[item.hem_type]}\n${
													description[item.hem_type]
												}`}
											>
												<Info size={12} />
											</button>
										</label>
									</div>

									{/* Color Selection */}
									{item.hem_type !== "Plain Hem" && (
										<div className="space-y-2">
											<label className="block text-sm font-medium">
												Bar Colour
											</label>
											<select
												aria-label="Bar Colour"
												value={
													isSelected(item.hem_type.toLowerCase())
														? bottomBarColor
														: ""
												}
												onChange={handleBottomBarColor}
												className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
											>
												<option value="">--Select--</option>
												{item.colour.map((color) => (
													<option
														key={color}
														value={color}
														className={`
								${color === "Black" ? "text-white bg-[#000]" : ""}
								${color === "Brown" ? "text-white bg-[#665339]" : ""}
								${color === "Anodized" ? "bg-[#d3d2ce]" : ""}
							  `}
													>
														{color}
													</option>
												))}
											</select>
										</div>
									)}

									{/* Wrap Type Selection */}
									{item.hem_type.includes("Accubar") && (
										<div className="space-y-2">
											<label className="block text-sm font-medium">
												Fabric Wrapped
											</label>
											<select
												aria-label="Fabric Wrapped"
												value={
													isSelected(item.hem_type.toLowerCase())
														? selectedWrapType
														: ""
												}
												onChange={handleWrapType}
												className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
											>
												<option value="No">No</option>
												<option value="Yes">Yes</option>
											</select>
										</div>
									)}
								</div>
							</motion.div>
						))}
					</div>
				</div>
			</LoadingOverlay>
		</Customize_Reworked>
	);
};

export default Customize_BottomBar;
